import {ApiContext} from '@vf/utility/ApiContextProvider/ApiContextProvider';
import {ContactFlowSummary} from 'aws-sdk/clients/connect';
import React, {useContext, useEffect, useReducer} from 'react';
import {
  QueueFlowData,
  Menu,
  OpenCheckTypes,
  DefaultHoldMusicNames,
  QueueFlowActionTypes,
  DefaultInterruptSettings,
  QueueFlowTypes,
} from 'types/api/api-models';
import queueTreatmentConfig from './QueueTreatmentConfig';
import QueueTreatmentManagementContext from './QueueTreatmentContext';
import {
  queueTreatmentReducer,
  QueueTreatmentActions,
} from './queueTreatmentReducer';
import {IMessage} from './QueueTreatmentContextPropTypes';
import {IntervalListType} from 'modules/administrators/components/QueueTreatment/UpsertQueueTreatment';
export const queueTreatmentState = {
  ...queueTreatmentConfig,
};
export enum ENTITY_TYPES {
  FLOWS = 'flows',
  CUSTOMER_QUEUE_FLOWS = 'customerQueueFlows',
  QUEUES = 'queues',
  PROMPTS = 'prompts',
}

const QueueTreatmentManagementContextProvider: React.FC<React.ReactNode> = ({
  children,
}) => {
  const api = useContext(ApiContext);
  const [state, dispatch] = useReducer(
    queueTreatmentReducer,
    queueTreatmentState,
  );

  useEffect(() => {
    Promise.all([
      listQueueTreatments(),
      getQueues(),
      getFlows(),
      getPrompts(),
      getAvailableHours(),
      listMenus(),
      getNumbers(),
      getQueueFlowMappings(),
    ]);
  }, [api]);

  useEffect(() => {
    if (state.numbersLoaded && state.queueTreatmentLoaded) {
      const availNumbers = state.numbers.filter(
        (n) =>
          !state.queueTreatmentList.map((n) => n.key).includes(n.PhoneNumber),
      );
      dispatch({
        type: QueueTreatmentActions.SET_AVAILABLE_NUMBERS,
        payload: availNumbers,
      });
    }
  }, [
    state.numbers,
    state.numbersLoaded,
    state.queueTreatmentList,
    state.queueTreatmentLoaded,
  ]);

  useEffect(() => {
    if (state.availableHoursLoaded) {
      const customHours = state.availableHours.filter(
        (n) => n.openCheckType === OpenCheckTypes.CUSTOM,
      );
      dispatch({
        type: QueueTreatmentActions.SET_AVAILABLE_CUSTOM_HOURS,
        payload: customHours,
      });
    }
  }, [state.availableHours, state.availableHoursLoaded]);
  useEffect(() => {
    if (state.flowsLoaded) {
      const customerQueueFlows = state.flows.filter(
        (flow: ContactFlowSummary) => flow.ContactFlowType === 'CUSTOMER_QUEUE',
      );
      dispatch({
        type: QueueTreatmentActions.SET_CUSTOMER_QUEUE_FLOWS,
        payload: {customerQueueFlows},
      });
    }
  }, [state.flowsLoaded]);

  const getQueueMetadata = (queueId: string) => {
    const queue = state.queues.find((queue) => queue.Id === queueId);
    return queue;
  };
  const getEntityAttrValue = (
    type: ENTITY_TYPES,
    keyType: string,
    keyValue: string,
    returnAttr: string = null,
  ): any => {
    const item = state[type].find((o) => o[keyType] === keyValue);
    if (!item) return '';
    return returnAttr ? item[returnAttr] : item;
  };

  const listMenus = async () => {
    dispatch({type: QueueTreatmentActions.LOADING_MENUS, payload: true});
    const menuList = await api.menu.listMenus();
    dispatch({type: QueueTreatmentActions.LIST_MENUS, payload: {menuList}});
  };

  const listQueueTreatments = async () => {
    dispatch({
      type: QueueTreatmentActions.LOADING_QUEUE_TREATMENT,
      payload: true,
    });
    const queueTreatmentList = await api.queueTreatment.listQueueFlowData();
    dispatch({
      type: QueueTreatmentActions.LIST_QUEUE_TREATMENT,
      payload: {queueTreatmentList},
    });
  };
  const getQueueFlowMappings = async () => {
    dispatch({
      type: QueueTreatmentActions.LOADING_QUEUE_FLOW_MAPPER,
      payload: true,
    });
    const queueFlowMapper = await api.queueTreatment.getQueueFlowMapping();
    dispatch({
      type: QueueTreatmentActions.SET_QUEUE_FLOW_MAPPER,
      payload: {queueFlowMapper},
    });
  };
  const createMenuScaffolding = async (menu: Menu) => {
    const res = await api.menu.createMenu(menu);
    await listMenus();
  };

  const getNumbers = async () => {
    try {
      const numbers = await api.helpers.getAllNumbers();
      dispatch({
        type: QueueTreatmentActions.GET_NUMBERS,
        payload: {
          numbers,
        },
      });
    } catch (err) {
      console.warn('Unable to load claimed numbers', err);
    }
  };

  const getQueues = async () => {
    try {
      const queues = await api.helpers.getAllQueues();
      dispatch({
        type: QueueTreatmentActions.GET_QUEUES,
        payload: {
          queues,
        },
      });
    } catch (err) {
      console.warn('Unable to load queues', err);
    }
  };
  const getAvailableHours = async () => {
    try {
      const availableHours = await api.hour.getAvailableHoursSelections();
      dispatch({
        type: QueueTreatmentActions.GET_AVAILABLE_HOURS,
        payload: {
          availableHours,
        },
      });
    } catch (err) {
      console.warn('Unable to load available hours', err);
    }
  };
  const getFlows = async () => {
    try {
      const flows = await api.helpers.getAllFlows();
      dispatch({
        type: QueueTreatmentActions.GET_FLOWS,
        payload: {
          flows,
        },
      });
    } catch (err) {
      console.warn('Unable to load queues', err);
    }
  };
  const getPrompts = async () => {
    try {
      const prompts = await api.helpers.getAllPrompts();
      dispatch({
        type: QueueTreatmentActions.GET_PROMPTS,
        payload: {
          prompts,
        },
      });
    } catch (err) {
      console.warn('Unable to load prompts', err);
    }
  };
  const getQueueTreatment = async (queueTreatmentName: string) => {
    const workingQueueTreatment = await api.queueTreatment.getQueueFlowData(
      queueTreatmentName,
    );
    dispatch({
      type: QueueTreatmentActions.GET,
      payload: {workingQueueTreatment},
    });
  };
  const deleteQueueTreatment = async (queueTreatmentName: string) => {
    dispatch({
      type: QueueTreatmentActions.DELETING_QUEUE_TREATMENT,
      payload: true,
    });
    try {
      await api.queueTreatment.deleteQueueFlowData(queueTreatmentName);
      dispatch({
        type: QueueTreatmentActions.DELETE,
        payload: {queueTreatmentName},
      });
      await listQueueTreatments();
    } catch (err) {
      dispatch({
        type: QueueTreatmentActions.DELETING_QUEUE_TREATMENT,
        payload: false,
      });
    }
  };
  const upsertQueueTreatment = (queueTreatment: QueueFlowData) => {
    // @todo CALL API HERE
    dispatch({type: QueueTreatmentActions.UPSERT, payload: {queueTreatment}});
  };

  const createQueueTreatmentScaffolding = async (
    queueTreatment: QueueFlowData,
  ) => {
    const res = await api.queueTreatment.createQueueFlowData(queueTreatment);
    await listQueueTreatments();
  };

  const setMsg = (msg: IMessage) => {
    dispatch({type: QueueTreatmentActions.SET_MESSAGE, payload: {msg}});
  };

  const getQueueFlowMapping = (
    interruptSetting: number,
    holdMusic: string,
    actionType: QueueFlowActionTypes,
    id: boolean = false,
  ) => {
    if (
      !state.queueFlowMapper ||
      !state.queueFlowMapper.default ||
      !interruptSetting ||
      !holdMusic ||
      !actionType
    )
      return '';
    if (actionType === QueueFlowActionTypes.BASIC_HOLD) {
      const map = state.queueFlowMapper.default[QueueFlowTypes.BASIC_HOLD];
      return id ? map[holdMusic].split('/')[3] : map[holdMusic];
    } else {
      const map =
        state.queueFlowMapper.default[QueueFlowTypes.INTERRUPT_ACTION];
      return id
        ? map[`${interruptSetting}_SECONDS`][holdMusic].split('/')[3]
        : map[`${interruptSetting}_SECONDS`][holdMusic];
    }
  };

  return (
    <QueueTreatmentManagementContext.Provider
      value={{
        ...state,
        upsertQueueTreatment,
        getQueueTreatment,
        listQueueTreatments,
        deleteQueueTreatment,
        getQueueMetadata,
        getEntityAttrValue,
        createQueueTreatmentScaffolding,
        createMenuScaffolding,
        setMsg,
        getQueueFlowMapping,
      }}>
      {children}
    </QueueTreatmentManagementContext.Provider>
  );
};

export default QueueTreatmentManagementContextProvider;
